// src/components/MediaDisplay.tsx

import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { getMediaUrl } from '../../hooks/useMediaUpload';

interface MediaDisplayProps {
  fileId: string;
  variant?: 'thumbnail' | 'small' | 'medium' | 'large' | 'original';
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  useThumbnailForPreview?: boolean;
  isVideo?: boolean;
  controls?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
}

/**
 * MediaDisplay component - Displays uploaded media with loading states
 *
 * Features:
 * - Shows loading spinner while media loads
 * - Handles both images and videos
 * - Supports multiple variants (thumbnail, small, medium, large, original)
 * - Error handling with retry
 */
export const MediaDisplay: React.FC<MediaDisplayProps> = ({
  fileId,
  variant = 'original',
  alt = 'Uploaded media',
  className = '',
  style = {},
  useThumbnailForPreview = false,
  isVideo = false,
  controls = true,
  autoPlay = false,
  loop = false,
  muted = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const mediaUrl = getMediaUrl(fileId, variant);
  const thumbnailUrl = useThumbnailForPreview ? getMediaUrl(fileId, 'thumbnail') : mediaUrl;

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [fileId, variant]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setIsLoading(true);
    setHasError(false);
  };

  if (hasError) {
    return (
      <div
        className={`flex flex-col items-center justify-center bg-gray-800 text-white ${className}`}
        style={style}
      >
        <AlertCircle size={32} className="mb-2 opacity-50" />
        <p className="text-sm opacity-70 mb-2">Failed to load media</p>
        <button
          onClick={handleRetry}
          className="px-3 py-1 text-xs bg-white/10 hover:bg-white/20 rounded transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={style}>
      {isLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-gray-800/50 z-10"
        >
          <Loader2 size={32} className="animate-spin text-white opacity-70" />
        </div>
      )}

      {isVideo ? (
        <video
          src={`${mediaUrl}${mediaUrl.includes('?') ? '&' : '?'}retry=${retryCount}`}
          className="w-full h-full object-cover"
          controls={controls}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          onLoadedData={handleLoad}
          onError={handleError}
          style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s' }}
        />
      ) : (
        <img
          src={`${mediaUrl}${mediaUrl.includes('?') ? '&' : '?'}retry=${retryCount}`}
          alt={alt}
          className="w-full h-full object-cover"
          onLoad={handleLoad}
          onError={handleError}
          style={{ opacity: isLoading ? 0 : 1, transition: 'opacity 0.3s' }}
        />
      )}
    </div>
  );
};

/**
 * MediaThumbnail component - Optimized for showing thumbnails in lists
 */
export const MediaThumbnail: React.FC<Omit<MediaDisplayProps, 'variant'>> = (props) => {
  return <MediaDisplay {...props} variant="thumbnail" />;
};

/**
 * MediaPreview component - For showing small previews
 */
export const MediaPreview: React.FC<Omit<MediaDisplayProps, 'variant'>> = (props) => {
  return <MediaDisplay {...props} variant="small" />;
};

/**
 * MediaFull component - For showing full-size media
 */
export const MediaFull: React.FC<Omit<MediaDisplayProps, 'variant'>> = (props) => {
  return <MediaDisplay {...props} variant="original" />;
};
